/**
 * spec/admin-ui/navigation.md: admin-ui-navigation-R18–R20, R26, R27, R32, R48–R50.
 * spec/server/control/auth.md: control-auth-R66, R73, R76, R80.
 * spec/server/control/response-format.md: control-response-format-R08.
 */
import type { SessionInfo } from '@/types/auth'
import type { VersionInfo } from '@/types'
import { resolveAuthBaseUrl, resolveApiBaseUrl } from '@/utils/api-base-url'

const errors: Record<string, string> = {
  invalid_input: 'The submitted values were not accepted.',
  unauthenticated: 'Your session has ended. Please sign in again.',
  forbidden: 'This operation is not permitted.',
  not_found: 'The requested object does not exist.',
  conflict: 'The operation conflicts with the current state.',
  internal_error: 'The server encountered an internal error.',
  license_invalid: 'The installation license is invalid. Requests cannot be performed.',
}
export class AuthRequestError extends Error {
  constructor(readonly code: string, message: string) { super(message) }
}

async function request(path: string, method: 'GET' | 'POST'): Promise<Response> {
  let response: Response
  try {
    response = await fetch(path, {
      method, credentials: 'include', signal: AbortSignal.timeout(30_000),
    })
  } catch {
    throw new AuthRequestError('network', 'No response was received from the server.')
  }
  if (response.ok && response.status !== 200) throw invalidResponse()
  if (!response.ok) {
    const body: unknown = await response.json().catch((): null => null)
    const code = typeof body === 'object' && body !== null && 'error' in body ? body.error : null
    throw new AuthRequestError(typeof code === 'string' ? code : '',
      typeof code === 'string' && Object.hasOwn(errors, code)
        ? errors[code]! : 'The server rejected the request without a reason.')
  }
  return response
}
const invalidResponse = (): AuthRequestError =>
  new AuthRequestError('invalid_response', 'The server rejected the request without a reason.')

export async function getSessionEffect(): Promise<SessionInfo> {
  const response = await request(`${resolveAuthBaseUrl()}/session`, 'GET')
  const data: unknown = await response.json().catch((): null => null)
  if (typeof data !== 'object' || data === null ||
    !('user_id' in data) || typeof data.user_id !== 'string' ||
    !('login' in data) || typeof data.login !== 'string' ||
    !('privileges' in data) || !Array.isArray(data.privileges) ||
    !data.privileges.every((value: unknown): value is string => typeof value === 'string') ||
    !('end_session_url' in data) || typeof data.end_session_url !== 'string') throw invalidResponse()
  let logoutUrl: URL
  try { logoutUrl = new URL(data.end_session_url) } catch { throw invalidResponse() }
  if (!['http:', 'https:'].includes(logoutUrl.protocol)) throw invalidResponse()
  // Copy only the documented fields: never retain tokens or untrusted extra data.
  return { user_id: data.user_id, login: data.login,
    privileges: [...data.privileges], end_session_url: data.end_session_url }
}
export async function logoutEffect(): Promise<void> {
  await request(`${resolveAuthBaseUrl()}/logout`, 'POST')
}
export function navigateEffect(url: string): void { window.location.assign(url) }
export function beginLoginEffect(returnTo: string): void {
  navigateEffect(`${resolveAuthBaseUrl()}/login?${new URLSearchParams({ return_to: returnTo })}`)
}
export function currentPanelAddress(): string {
  return window.location.pathname + window.location.search + window.location.hash
}
export function removeLegacyAuthEffect(): void {
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of ['access_token', 'refresh_token', 'username']) storage.removeItem(key)
  }
}
export async function getVersionEffect(): Promise<VersionInfo> {
  const response = await request(`${resolveApiBaseUrl()}/api/version`, 'GET')
  const data: unknown = await response.json().catch((): null => null)
  if (typeof data !== 'object' || data === null || !('version' in data) ||
    typeof data.version !== 'string') throw invalidResponse()
  return { version: data.version,
    commit: 'commit' in data && typeof data.commit === 'string' ? data.commit : undefined }
}
